import { useMemo } from 'react';

import type { ServiceForPicker } from './types';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import { findInArrayBy, toMoneyFormat } from '@/helpers';

type ServicesListProps = {
  services: Record<string, ServiceForPicker[]>;
  selectedServices: ServiceForPicker[];
  expandedCategory: string | undefined;
  onCategoryClick: (category: string) => void;
  onServiceClick: (service: ServiceForPicker) => void;
};

const Service = (props: {
  service: ServiceForPicker;
  selectedServices: ServiceForPicker[];
  onClick: (service: ServiceForPicker) => void;
}) => {
  const { service, selectedServices, onClick } = props;

  const handleClick = () => {
    onClick(service);
  };

  const checked = useMemo(() => {
    return !!findInArrayBy(selectedServices, service.id);
  }, [selectedServices, service.id]);

  const labelId = `service-${service.id}-label`;

  return (
    <Label
      htmlFor={labelId}
      className="hover:bg-accent flex cursor-pointer items-center gap-3 rounded-md px-3 py-2"
    >
      <Checkbox
        checked={checked}
        id={labelId}
        onCheckedChange={handleClick}
      />
      <span className="flex flex-1 items-center justify-between">
        <span>{service.name}</span>
        <span className="text-muted-foreground ml-2">
          {toMoneyFormat(service.price)}
        </span>
      </span>
    </Label>
  );
};

export const ServicesList = (props: ServicesListProps) => {
  const {
    services,
    selectedServices,
    expandedCategory,
    onCategoryClick,
    onServiceClick,
  } = props;

  return (
    <div className="w-full overflow-y-auto">
      <Accordion
        type="single"
        collapsible
        value={expandedCategory}
        onValueChange={(value) => {
          onCategoryClick(value ?? '');
        }}
      >
        {Object.keys(services).map((category) => (
          <AccordionItem
            key={category}
            value={category}
          >
            <AccordionTrigger>{category}</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-1">
                {services[category].map((service) => (
                  <Service
                    key={service.id}
                    service={service}
                    selectedServices={selectedServices}
                    onClick={onServiceClick}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
